import '../index.css';
import { i18n } from './services/i18n.js';
import { auth } from './services/auth.js';
import { icon } from './services/icons.js';

// Keep the client bundle versioned independently from Cloudflare's SPA fallback cache.

// Import views
import { HomeView } from './views/home.js';
import { LoginView } from './views/login.js';
import { DashboardView } from './views/dashboard.js';
import { GameEditorView } from './views/gameEditor.js';
import { LobbyView } from './views/lobby.js';
import { PresentView } from './views/present.js';
import { JoinView } from './views/join.js';
import { PlayView } from './views/play.js';
import { LeaderboardView } from './views/leaderboard.js';
import { PodiumView } from './views/podium.js';
import { ReportView } from './views/report.js';
import { SettingsView } from './views/settings.js';
import { AdminView } from './views/admin.js';
import { AdminQuizzesView } from './views/adminQuizzes.js';
import { AdminSessionsView } from './views/adminSessions.js';
import { AdminHostsView } from './views/adminHosts.js';
import { AdminSystemView } from './views/adminSystem.js';
import { SupportView } from './views/support.js';

// Global Toast notification system
window.showToast = (message, duration = 3000) => {
  const existing = document.querySelector('.toast');
  if (existing) existing.remove();

  const toastEl = document.createElement('div');
  toastEl.className = 'toast';
  toastEl.innerText = message;
  document.body.appendChild(toastEl);
  
  // Trigger animation
  setTimeout(() => toastEl.classList.add('show'), 100);
  
  // Remove after duration
  setTimeout(() => {
    toastEl.classList.remove('show');
    setTimeout(() => toastEl.remove(), 300);
  }, duration);
};

// Routing logic
const routes = {
  '/': HomeView,
  '/login': LoginView,
  '/dashboard': DashboardView,
  '/games/new': GameEditorView,
  '/games/:id': GameEditorView,
  '/sessions/:id/lobby': LobbyView,
  '/sessions/:id/present': PresentView,
  '/join': JoinView,
  '/play': PlayView,
  '/leaderboard': LeaderboardView,
  '/podium': PodiumView,
  '/reports/:id': ReportView,
  '/settings': SettingsView,
  '/support': SupportView,
  '/admin': AdminView,
  '/admin/quizzes': AdminQuizzesView,
  '/admin/sessions': AdminSessionsView,
  '/admin/hosts': AdminHostsView,
  '/admin/system': AdminSystemView
};

const escapeHtml = (value = '') => String(value).replace(/[&<>'"]/g, character => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
}[character]));

// Parse routes with parameters (e.g. /games/:id)
const parseRoute = (hash) => {
  const path = (hash.slice(1).split('?')[0] || '/');
  
  // Find matching route
  for (const route in routes) {
    const routeParts = route.split('/');
    const pathParts = path.split('/');
    
    if (routeParts.length !== pathParts.length) continue;
    
    const params = {};
    let isMatch = true;
    
    for (let i = 0; i < routeParts.length; i++) {
      if (routeParts[i].startsWith(':')) {
        params[routeParts[i].slice(1)] = pathParts[i];
      } else if (routeParts[i] !== pathParts[i]) {
        isMatch = false;
        break;
      }
    }
    
    if (isMatch) {
      return { view: routes[route], params };
    }
  }
  
  return { view: HomeView, params: {} };
};

const renderLayout = () => {
  const hash = window.location.hash;
  const { view, params } = parseRoute(hash);
  const path = (hash.slice(1).split('?')[0] || '/');
  const isAdminView = path.startsWith('/admin');
  const lang = i18n.getLang();
  document.documentElement.lang = lang;
  const robotsMeta = document.querySelector('meta[name="robots"]');
  if (robotsMeta) robotsMeta.content = path === '/'
    ? 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
    : 'noindex, nofollow';
  const routeTitles = {
    '/login': i18n.t('login_title'), '/dashboard': i18n.t('dashboard_title'),
    '/games/new': i18n.t('create_game_title'), '/join': i18n.t('join_title'),
    '/settings': i18n.t('account_settings'), '/support': i18n.t('support_title'), '/admin': 'Admin',
    '/admin/quizzes': 'Admin Quizzes', '/admin/sessions': 'Admin Sessions',
    '/admin/hosts': 'Admin Hosts', '/admin/system': 'Admin System'
  };
  if (path === '/') {
    document.title = lang === 'vi'
      ? 'Heart150 – Nền tảng quiz realtime cho lớp học và sự kiện'
      : 'Heart150 – Realtime quizzes for classrooms and events';
  } else {
    const matchedTitle = routeTitles[path] || (path.startsWith('/games/') ? i18n.t('edit_game_title')
      : path.startsWith('/reports/') ? i18n.t('report_title') : 'Heart150');
    document.title = `${matchedTitle} | Heart150`;
  }
  
  const appContainer = document.getElementById('app');
  appContainer.innerHTML = ''; // Clear previous

  // Hide main nav for presentation/player screens
  const isMinimalView = path.startsWith('/sessions/') || path.startsWith('/play') || path.startsWith('/leaderboard') || path.startsWith('/podium');
  const storedUser = localStorage.getItem('h150_auth_user');
  const currentUser = storedUser ? JSON.parse(storedUser) : null;

  // A signed-in Host should never remain on the login screen. This also covers
  // reopening /#/login after OAuth has already established the session.
  if (path === '/login' && currentUser) {
    window.location.replace(`${window.location.pathname}${window.location.search}#/dashboard`);
    return;
  }

  if (!isMinimalView) {
    const nav = document.createElement('nav');
    nav.className = 'navbar';
    
    const accountName = currentUser?.name || currentUser?.email || 'Host';
    const accountAvatar = currentUser?.avatar
      ? `<img src="${escapeHtml(currentUser.avatar)}" alt="" referrerpolicy="no-referrer">`
      : `<span class="account-initial">${escapeHtml(accountName.trim().charAt(0).toUpperCase())}</span>`;
    
    nav.innerHTML = `
      <div class="container" style="display:flex; justify-content:space-between; align-items:center; width:100%;">
        <a href="#/" class="logo" aria-label="Heart150 home"><span class="logo-mark">${icon('heart', 'logo-icon')}</span><span class="logo-word">Heart<span>150</span></span></a>
        
        <div class="nav-links">
          ${currentUser ? `
            <a href="#/dashboard" class="nav-link ${path === '/dashboard' ? 'active' : ''}" ${path === '/dashboard' ? 'aria-current="page"' : ''}>Dashboard</a>
            ${currentUser.isAdmin ? `<a href="#/admin" class="nav-link ${isAdminView ? 'active' : ''}" ${isAdminView ? 'aria-current="page"' : ''}>Admin</a>` : ''}
            <a href="#/support" class="nav-link ${path === '/support' ? 'active' : ''}" ${path === '/support' ? 'aria-current="page"' : ''}>Support</a>
            <div class="account-menu">
              <button id="accountMenuButton" class="account-trigger" type="button" aria-haspopup="menu" aria-expanded="false">
                <span class="account-avatar">${accountAvatar}</span>
                <span class="account-identity">
                  <strong>${escapeHtml(accountName)}</strong>
                  ${isAdminView ? '' : `<small>${i18n.t('hello_host')}</small>`}
                </span>
                ${icon('chevronDown', 'account-chevron')}
              </button>
              <div id="accountDropdown" class="account-dropdown" role="menu" hidden>
                <a href="#/settings" role="menuitem">${icon('cog')}<span>${isAdminView ? 'Settings' : i18n.t('settings')}</span></a>
                <button id="logoutBtn" type="button" role="menuitem">${icon('logout')}<span>${isAdminView ? 'Log out' : i18n.t('logout')}</span></button>
              </div>
            </div>
          ` : `
            <a href="#/join" class="nav-link ${path === '/join' ? 'active' : ''}">${i18n.t('join_title')}</a>
            <a href="#/support" class="nav-link ${path === '/support' ? 'active' : ''}" ${path === '/support' ? 'aria-current="page"' : ''}>Support</a>
            <a href="#/login" class="btn btn-primary nav-cta">${i18n.t('login_title')}</a>
          `}
        </div>
      </div>
    `;
    
    appContainer.appendChild(nav);

    // Bind event handlers
    const logoutBtn = nav.querySelector('#logoutBtn');
    if (logoutBtn) {
      logoutBtn.addEventListener('click', async () => {
        await auth.logout();
        window.showToast(i18n.t('logout_success'));
        window.location.hash = '#/';
      });
    }

    const accountButton = nav.querySelector('#accountMenuButton');
    const accountDropdown = nav.querySelector('#accountDropdown');
    if (accountButton && accountDropdown) {
      const closeAccountMenu = () => {
        accountDropdown.hidden = true;
        accountButton.setAttribute('aria-expanded', 'false');
      };
      accountButton.addEventListener('click', (event) => {
        event.stopPropagation();
        const willOpen = accountDropdown.hidden;
        accountDropdown.hidden = !willOpen;
        accountButton.setAttribute('aria-expanded', String(willOpen));
        if (willOpen) document.addEventListener('click', closeAccountMenu, { once: true });
      });
      accountDropdown.addEventListener('click', event => event.stopPropagation());
      accountButton.addEventListener('keydown', event => {
        if (event.key === 'Escape') closeAccountMenu();
      });
    }
  }

  // Render view body
  const viewContainer = document.createElement('main');
  viewContainer.style.flex = '1';
  viewContainer.style.display = 'flex';
  viewContainer.style.flexDirection = 'column';
  
  appContainer.appendChild(viewContainer);
  
  // Instantiate the view
  view(viewContainer, params);

  // Render footer if not a minimal view
  if (!isMinimalView) {
    const footer = document.createElement('footer');
    footer.className = 'footer';
    footer.innerHTML = `
      <div class="container footer-container">
        <div class="footer-brand">
          <a href="#/" class="logo" aria-label="Heart150 home"><span class="logo-mark">${icon('heart', 'logo-icon')}</span><span class="logo-word">Heart<span>150</span></span></a>
          <p>Live quizzes that turn every room into a shared experience.</p>
        </div>
        <nav class="footer-links" aria-label="Footer navigation">
          <a href="#/join" class="footer-link">Join a game</a>
          <a href="#/support" class="footer-link">Support</a>
          ${currentUser ? '<a href="#/dashboard" class="footer-link">Dashboard</a>' : '<a href="#/login" class="footer-link">Host sign in</a>'}
          ${currentUser?.isAdmin ? '<a href="#/admin" class="footer-link">Admin</a>' : ''}
        </nav>
      </div>
      <div class="footer-bottom">
        <div class="container footer-meta">
          <span>© 2026 Heart150</span>
          <span>Made for moments that matter.</span>
        </div>
      </div>
    `;
    appContainer.appendChild(footer);
  }
};

// Listeners
window.addEventListener('hashchange', () => {
  window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  renderLayout();
});
window.addEventListener('languageChanged', renderLayout);

window.addEventListener('DOMContentLoaded', async () => {
  document.documentElement.dataset.app = 'heart150';
  document.documentElement.dataset.build = 'admin-pages-20260722b';
  renderLayout();
  try {
    const hadCachedUser = Boolean(localStorage.getItem('h150_auth_user'));
    const user = await auth.me();
    if (user) localStorage.setItem('h150_auth_user', JSON.stringify(user));
    else localStorage.removeItem('h150_auth_user');
    if (hadCachedUser !== Boolean(user)) renderLayout();
  } catch (error) {
    console.warn('[Auth] Background session check failed:', error.message);
  }
});
